import { useMemo } from 'react'
import {
    useLocation,
    useParams,
    useSearchParams,
    Navigate,
} from 'react-router-dom'
import { Grid } from '@design-systems/canvas-ui'
import { Table } from 'components/table-compat/table-compat'
import { useEssentials } from 'hooks/utils/useEssentials'
import {
    buildMetricDetailTableData,
    getMetricDisplayName,
    buildStoreTableData,
} from './helpers'
import { buildDataTableObject } from 'utils/data'
import { EnrichedMetric } from './types'
import { useMyTargetPerformanceDetails } from 'hooks/myTarget-performance/useMyTargetPerformanceDetails'
import { useMyTargetPerformanceDetailStores } from 'hooks/myTarget-performance/useMyTargetPerformanceDetailStores'
import { useMyTargetPerformanceDetailGreenStores } from 'hooks/myTarget-performance/useMyTargetPerformanceDetailGreenStores'
import { Constants } from 'constants/constants'
import { BasicPlaceholder } from 'components/placeholder'
import { ErrorAlert } from 'components/error-fallback'
import { DETAIL_TIME_DIMENSIONS } from './constants'
import { isGroup } from 'utils/location'
import { GeneralizedComponent } from 'components/generalized-component/generalized-component'
import { TRANSLATION_KEYS } from 'locales/translation-keys'

const TAB_CARD_MAP: Record<string, string> = {
    guest: Constants.GREENFIELD_CARDS.MY_TARGET_PERFORMANCE.GUEST_METRICS,
    financial:
        Constants.GREENFIELD_CARDS.MY_TARGET_PERFORMANCE.FINANCIAL_METRICS,
    team: Constants.GREENFIELD_CARDS.MY_TARGET_PERFORMANCE.TEAM_METRICS,
}

export const MetricDetails = () => {
    const location = useLocation()
    const { metricName: metricNameParam } = useParams()
    const [searchParams] = useSearchParams()
    const { t, env, session, currentLocation } = useEssentials()

    const metric = location.state?.metric as EnrichedMetric | undefined
    const metricName = metric?.metric_name || metricNameParam || ''
    const tabName = searchParams.get('tab') || 'guest'

    const cardNumber = TAB_CARD_MAP[tabName] || TAB_CARD_MAP.guest
    const isGroupLocation = isGroup(currentLocation)

    // Fetch district-level data (default view)
    const {
        dataByTimeDimension: districtData,
        isLoading: isLoadingDistricts,
        isError: isErrorDistricts,
        error: errorDistricts,
        refetch: refetchDistricts,
    } = useMyTargetPerformanceDetails({
        env,
        accessToken: session?.accessToken || '',
        location: currentLocation,
        cardNumber,
    })

    // Fetch store-level data (only for group locations)
    const {
        dataByTimeDimension: storeData,
        isLoading: isLoadingStores,
        isError: isErrorStores,
        error: errorStores,
        refetch: refetchStores,
    } = useMyTargetPerformanceDetailStores({
        env,
        accessToken: session?.accessToken || '',
        location: currentLocation,
        tab: tabName,
        enabled: isGroupLocation,
    })

    const {
        greenStoresData,
        isLoading: isLoadingGreenStores,
        // Green stores failure is non-fatal — degrade gracefully by omitting
        // the % Green Stores column rather than blocking the whole detail view.
    } = useMyTargetPerformanceDetailGreenStores({
        env,
        accessToken: session?.accessToken || '',
        location: currentLocation,
    })

    const hasGreenStoresData = Object.values(greenStoresData).some(
        (d) => d?.rows?.length,
    )

    // Build district table data
    const districtTableData = useMemo(
        () =>
            buildMetricDetailTableData({
                metricName,
                timeDimensions: DETAIL_TIME_DIMENSIONS,
                dataByTimeDimension: districtData,
                greenStoresData,
                hasGreenStoresData,
                currentLocation,
                tabName,
                t,
            }),
        [
            metricName,
            districtData,
            greenStoresData,
            hasGreenStoresData,
            currentLocation,
            tabName,
            t,
        ],
    )

    // Build store table data (only for group locations, without green stores)
    const storeTableData = useMemo(
        () =>
            buildStoreTableData({
                isGroupLocation,
                metricName,
                timeDimensions: DETAIL_TIME_DIMENSIONS,
                dataByTimeDimension: storeData,
                currentLocation,
                tabName,
                t,
            }),
        [metricName, storeData, currentLocation, tabName, t, isGroupLocation],
    )

    // Configure sortable columns - all sortable except first column (location)
    const columnSortSettings = useMemo(() => {
        const numColumns = districtTableData?.headings?.length || 0
        const settings: Record<
            number,
            { sortable: boolean; sortType?: 'numeric' }
        > = {}

        for (let i = 0; i < numColumns; i++) {
            if (i === 0) {
                // First column (location) - not sortable
                settings[i] = { sortable: false }
            } else {
                // All other columns - sortable as numeric
                settings[i] = { sortable: true, sortType: 'numeric' }
            }
        }

        return settings
    }, [districtTableData?.headings?.length])

    if (!metricName) {
        return <Navigate to="/mytarget-performance" replace />
    }

    const isLoading =
        isLoadingDistricts ||
        isLoadingGreenStores ||
        (isGroupLocation && isLoadingStores)
    const isError = isErrorDistricts || (isGroupLocation && isErrorStores)
    const error = errorDistricts || errorStores

    if (isLoading) {
        return (
            <div className="placeholder-container hc8-pt-6x">
                <BasicPlaceholder type="table" rows={5} columns={3} />
            </div>
        )
    }

    if (isError) {
        return (
            <div className="hc8-pt-6x">
                <ErrorAlert
                    error={error}
                    onRetry={() => {
                        refetchDistricts()
                        if (isGroupLocation) {
                            refetchStores()
                        }
                    }}
                />
            </div>
        )
    }

    const widths =
        districtTableData?.headings?.map((_, i) => (i === 0 ? 65 : 80)) ?? []

    const tableName =
        metric?.displayName ||
        getMetricDisplayName(Object.values(districtData)[0], metricName)

    // For group locations, use GeneralizedComponent with toggle
    // Districts shown first (default), toggle shows "show stores"
    if (isGroupLocation && storeTableData) {
        const payload = {
            [t(TRANSLATION_KEYS.storeFulfillment.toggles.showDistricts)]: [
                {
                    title: '',
                    subtitle: '',
                    data: {
                        '': districtTableData,
                    },
                },
            ],
            [t(TRANSLATION_KEYS.storeFulfillment.toggles.showStores)]: [
                {
                    title: '',
                    subtitle: '',
                    data: {
                        '': storeTableData,
                    },
                },
            ],
        }

        return (
            <Grid.Container className="hc8-pa-3x">
                <Grid.Item
                    size={{
                        xs: 12,
                    }}
                >
                    <GeneralizedComponent
                        payload={payload}
                        widths={widths}
                        pinFirstColumn={true}
                        scrollableWidth={true}
                        alternateRowColor={true}
                        columnSortSettingsByIndex={columnSortSettings}
                        kpiName="myTarget performance metric details"
                    />
                </Grid.Item>
            </Grid.Container>
        )
    }

    // For non-group locations, use simple Table
    return (
        <Grid.Container className="hc8-pa-3x">
            {districtTableData && (
                <Grid.Item
                    className="hc8-pa-none"
                    size={{
                        xs: 12,
                    }}
                >
                    <Table
                        data={buildDataTableObject({
                            currentData: districtTableData,
                            widths,
                            columnSortSettingsByIndex: columnSortSettings,
                            pinFirstColumn: true,
                        })}
                        scrollableWidth={true}
                        alternateRowColor={true}
                        name={tableName}
                    />
                </Grid.Item>
            )}
        </Grid.Container>
    )
}
